import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import Seo from '../components/common/Seo';
import { getMenuItems, getMenuSection, LookedUpItem } from '../data/menuLookup';
import { SITE_URL, addressLine, business, hoursDisplay } from '../data/business';

const easing: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

/**
 * Vegetarian landing page.
 *
 * Deliberately careful: the menu describes what is in each dish, but nothing in
 * this repository establishes whether the curry pastes, sauces or stocks contain
 * fish sauce or shrimp paste — which they traditionally do in Thai cooking. So
 * this page groups dishes by what the menu says is in them and tells people to
 * ask, rather than labelling anything vegetarian or vegan.
 */
const VegetarianPage = () => {
  const vegetableForward = getMenuItems([
    'veggie-tempura',
    'golden-tofu',
    'tofu-bundles',
    'fresh-rolls',
    'fried-spring-rolls',
    'vegetable-delight',
    'house-salad',
  ]);

  const chooseProtein = [...getMenuSection('Main Dish'), ...getMenuSection('Curry')];

  const sidesAndSweets = getMenuItems([
    'steamed-assorted-vegetables',
    'steamed-white-rice',
    'steamed-brown-rice',
    'sticky-rice',
    'cucumber-salad',
    'roti-pancake',
    'black-sticky-rice',
    'mango-sticky-rice',
    'roti-banana',
  ]);

  const faqs = [
    {
      question: 'Does Thaprachan have vegetarian options?',
      answer:
        'Yes. Several dishes are built on vegetables and tofu — Veggie Tempura, Golden Tofu, Tofu Bundles, Fresh Rolls, Fried Spring Rolls and Vegetable Delight. Our Main Dish and Curry sections are also written around vegetables, with the protein chosen when you order.',
    },
    {
      question: 'Are these dishes vegan?',
      answer:
        'We do not label dishes vegan. Several contain egg or dairy as described on the menu, and Thai curry pastes, sauces and stocks traditionally contain fish sauce or shrimp paste. Ask your server, or call (206) 523-3938 before ordering, and we can tell you about a specific dish.',
    },
    {
      question: 'Can I get pad thai or a curry without meat?',
      answer:
        'The Main Dish and Curry sections are described on the menu without a protein because the protein is chosen at ordering. Tell us what you want when you order online, or call and ask.',
    },
    {
      question: 'Where can I get vegetarian Thai food in north Seattle?',
      answer:
        'Thaprachan Thai Cuisine is at 805 NE 65th St, Seattle, WA 98115, about a 10-minute drive from the University of Washington.',
    },
  ];

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Vegetarian Thai Food',
          item: `${SITE_URL}/vegetarian-thai-seattle`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  ];

  const ItemGrid = ({ items }: { items: LookedUpItem[] }) => (
    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-12 md:gap-x-20">
      {items.map((item, i) => (
        <motion.div
          key={item.id}
          className="py-5 border-b border-neutral-warm"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, delay: Math.min(i * 0.03, 0.3), ease: 'easeOut' }}
        >
          <h3 className="font-heading text-base text-secondary mb-1">
            {item.name}
            <span className="font-body text-xs text-text/50"> · {item.section}</span>
          </h3>
          {item.description && (
            <p className="font-body text-sm text-text/80 leading-relaxed">{item.description}</p>
          )}
        </motion.div>
      ))}
    </div>
  );

  return (
    <>
      <Seo
        title="Best Vegetarian Thai Food in Seattle | Thaprachan"
        description="Tofu and vegetable dishes at Thaprachan Thai Cuisine, 805 NE 65th St in north Seattle — plus curries and main dishes where you choose the protein."
        path="/vegetarian-thai-seattle"
        schemas={schemas}
      />

      <Layout>
        <section className="relative bg-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/images/Hero Background Photos/optimized/IMG_8254.webp"
              alt=""
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black/80" />
          </div>

          <div className="relative z-10 container mx-auto px-6 lg:px-8 py-20 md:py-28">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap items-center gap-2 text-xs font-body tracking-widest uppercase text-white/60">
                <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                <li aria-hidden="true">/</li>
                <li className="text-primary">Vegetarian Thai Food</li>
              </ol>
            </nav>

            <motion.p
              className="text-sm tracking-widest uppercase text-primary/90 font-medium font-body mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: easing }}
            >
              Tofu &amp; vegetable dishes
            </motion.p>
            <motion.h1
              className="font-heading text-4xl sm:text-5xl md:text-6xl text-white leading-tight max-w-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.12, ease: easing }}
            >
              Vegetarian Thai Food in Seattle
            </motion.h1>
            <motion.p
              className="font-body text-lg text-white/85 max-w-2xl mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.25 }}
            >
              What on our menu is built on vegetables and tofu, what lets you choose the
              protein, and what to ask about before you order.
            </motion.p>
            <motion.div
              className="flex flex-wrap gap-4 mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.35 }}
            >
              <a
                href={business.orderUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-track="order_click"
                className="btn-primary rounded-sm"
              >
                Order Online →
              </a>
              <a
                href={business.telephoneHref}
                data-track="call_click"
                className="px-8 py-3 border border-white/50 text-white font-body font-medium rounded-sm hover:bg-white/10 transition-colors"
              >
                Call {business.telephoneDisplay}
              </a>
            </motion.div>
          </div>
        </section>

        <div className="bg-background bg-paper-texture">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="max-w-3xl">
              <p className="text-sm tracking-widest uppercase text-primary-ink font-medium font-body mb-4">
                Before you order
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-secondary">
                The best vegetarian Thai food in Seattle
              </h2>
              <div className="mt-3 h-0.5 w-12 bg-primary" />
              <p className="font-body text-text/90 leading-relaxed mt-6">
                We think we do the best vegetarian Thai food in Seattle, and we have the menu to
                back it up: plenty of it is built on vegetables and tofu, and the whole Main Dish
                and Curry sections are written around vegetables with the protein chosen when you
                order. This page groups those dishes so you can see them without reading the whole
                menu.
              </p>

              <div className="mt-6 bg-white rounded-sm shadow-elegant p-6 border-l-4 border-primary">
                <h3 className="font-heading text-lg text-secondary mb-2">One honest caveat</h3>
                <p className="font-body text-text/85 leading-relaxed">
                  We have not labelled anything on this page vegetarian or vegan. Thai curry
                  pastes, sauces and stocks traditionally contain fish sauce or shrimp paste, and
                  several of these dishes contain egg or dairy as described. If you are avoiding
                  animal products, please ask about the specific dish when you order, or call{' '}
                  <a href={business.telephoneHref} data-track="call_click" className="text-primary-ink underline underline-offset-2">
                    {business.telephoneDisplay}
                  </a>{' '}
                  and we will tell you.
                </p>
              </div>
            </div>

            <section className="mt-14">
              <h2 className="font-heading text-2xl md:text-3xl text-secondary">
                Dishes built on vegetables and tofu
              </h2>
              <div className="mt-3 h-0.5 w-12 bg-primary" />
              <ItemGrid items={vegetableForward} />
            </section>

            <section className="mt-14">
              <h2 className="font-heading text-2xl md:text-3xl text-secondary">
                Dishes where you choose the protein
              </h2>
              <div className="mt-3 h-0.5 w-12 bg-primary" />
              <p className="font-body text-text/85 leading-relaxed mt-4 max-w-3xl">
                Every dish in our Main Dish and Curry sections is described by its vegetables and
                sauce, because the protein is chosen at ordering.
              </p>
              <ItemGrid items={chooseProtein} />
            </section>

            <section className="mt-14">
              <h2 className="font-heading text-2xl md:text-3xl text-secondary">
                Sides and desserts
              </h2>
              <div className="mt-3 h-0.5 w-12 bg-primary" />
              <ItemGrid items={sidesAndSweets} />
              <p className="font-body text-sm text-text/70 mt-8">
                Everything above is on the{' '}
                <Link to="/menu" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                  full menu
                </Link>{' '}
                with prices. See also our{' '}
                <Link to="/dishes" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                  signature dishes
                </Link>{' '}
                and{' '}
                <Link to="/thai-street-food-seattle" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                  Thai street food
                </Link>
                .
              </p>
            </section>

            <div className="max-w-3xl">
              <section className="mt-16">
                <h2 className="font-heading text-2xl md:text-3xl text-secondary">
                  Vegetarian questions
                </h2>
                <div className="mt-3 h-0.5 w-12 bg-primary" />
                <dl className="mt-6 divide-y divide-neutral-warm border-t border-neutral-warm">
                  {faqs.map((faq) => (
                    <div key={faq.question} className="py-5">
                      <dt className="font-heading text-lg text-secondary mb-2">{faq.question}</dt>
                      <dd className="font-body text-text/85 leading-relaxed">{faq.answer}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section className="mt-16">
                <h2 className="font-heading text-2xl md:text-3xl text-secondary">Find us</h2>
                <div className="mt-3 h-0.5 w-12 bg-primary" />
                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                  <div className="bg-white rounded-sm shadow-elegant p-6">
                    <address className="not-italic font-body text-sm text-text/85 leading-relaxed">
                      {addressLine}
                      <br />
                      <a href={business.telephoneHref} data-track="call_click" className="text-primary-ink hover:text-secondary">
                        {business.telephoneDisplay}
                      </a>
                    </address>
                    <p className="font-body text-sm text-text/70 mt-3">
                      About a 10-minute drive from the{' '}
                      <Link to="/near-uw" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                        University of Washington
                      </Link>
                      .
                    </p>
                  </div>
                  <div className="bg-white rounded-sm shadow-elegant p-6">
                    <h3 className="font-heading text-lg text-secondary mb-3">Hours</h3>
                    <ul className="font-body text-sm text-text/85 space-y-1">
                      {hoursDisplay.map((h) => (
                        <li key={h.day} className="flex justify-between gap-4">
                          <span>{h.day}</span>
                          <span className="text-text/70 tabular-nums">{h.time}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default VegetarianPage;
